import React, { Dispatch, SetStateAction } from 'react';
import {
  View, StyleSheet, Text, TextInput, FlatList,
} from 'react-native';
import Style from '../../../../constants/Style';
import Colors from '../../../../constants/Colors';
import {
  Day, constrainHours, constrainMinutes, pad,
} from './data';
import Switch from '../../../../components/Switch';

interface DayItemProps extends Day {
  toggle,
  setStartHours: Function,
  setStartMinutes: Function,
  setEndHours: Function,
  setEndMinutes: Function,
}

const DayItem = ({
  title, enabled, startHours, startMinutes, endHours, endMinutes, toggle, setStartHours, shortTitle,
  setStartMinutes, setEndHours, setEndMinutes,
}: DayItemProps) => <View
  style={[Style.row, styles.item, !enabled && styles.itemDisabled]}
>
    <View style={[Style.row, styles.titleSection]}>
      <Switch
        toggled={enabled}
        toggle={toggle}
        testID={`lm-whs-${shortTitle}-toggle-btn`}
      />
      <Text style={styles.title}>{title}</Text>
    </View>
    <View style={[Style.row, { flex: 1 }]}>
      <View style={[Style.row, styles.time]}>
        <TextInput
          value={startHours}
          onChangeText={(text: string) => setStartHours(constrainHours(text))}
          onBlur={() => setStartHours(pad(parseInt(startHours, 10)))}
          style={[styles.input, styles.text]}
          editable={enabled}
          maxLength={2}
          testID={`lm-whs-${shortTitle}-startw-hr-input-field`}
        />
        <Text style={[styles.colon, styles.text]}>:</Text>
        <TextInput
          value={startMinutes}
          onChangeText={(text: string) => setStartMinutes(constrainMinutes(text))}
          onBlur={() => setStartMinutes(pad(parseInt(startMinutes, 10)))}
          style={[styles.input, styles.text]}
          editable={enabled}
          maxLength={2}
          testID={`lm-whs-${shortTitle}-startw-min-input-field`}
        />
      </View>
      <View style={[Style.row, styles.time]}>
        <TextInput
          value={endHours}
          onChangeText={(text: string) => setEndHours(constrainHours(text))}
          onBlur={() => setEndHours(pad(parseInt(endHours, 10)))}
          style={[styles.input, styles.text]}
          editable={enabled}
          maxLength={2}
          testID={`lm-whs-${shortTitle}-stopw-hr-input-field`}
        />
        <Text style={[styles.colon, styles.text]}>:</Text>
        <TextInput
          value={endMinutes}
          onChangeText={(text: string) => setEndMinutes(constrainMinutes(text))}
          onBlur={() => setEndMinutes(pad(parseInt(endMinutes, 10)))}
          style={[styles.input, styles.text]}
          editable={enabled}
          maxLength={2}
          testID={`lm-whs-${shortTitle}-stopw-min-input-field`}
        />
      </View>
    </View>
  </View>;

interface WorkingHoursSettingsProps {
  dayList: Day[],
  setDayList: Dispatch<SetStateAction<Day[]>>,
}

const WorkingHoursSettings = ({
  dayList, setDayList,
}: WorkingHoursSettingsProps) => <View style={styles.container}>
    <View style={[Style.row, styles.header]}>
      <Text style={styles.parameterTitle}>
        {'DAY'}
      </Text>
      <View style={[Style.row, { flex: 1 }]}>
        <Text style={[styles.parameterTitle, styles.timeTitle]}>
          {'START'}
        </Text>
        <Text style={[styles.parameterTitle, styles.timeTitle]}>
          {'END'}
        </Text>
      </View>
    </View>
    <FlatList
      data={dayList}
      renderItem={({ item, index }) => <DayItem
        key={`day_item_${index}`}
        {...{ ...item }}
        setStartHours={(value: string) => {
          const updatedList: Day[] = [...dayList];
          updatedList[index].startHours = value;
          setDayList(updatedList);
        }}
        setStartMinutes={(value: string) => {
          const updatedList: Day[] = [...dayList];
          updatedList[index].startMinutes = value;
          setDayList(updatedList);
        }}
        setEndHours={(value: string) => {
          const updatedList: Day[] = [...dayList];
          updatedList[index].endHours = value;
          setDayList(updatedList);
        }}
        setEndMinutes={(value: string) => {
          const updatedList: Day[] = [...dayList];
          updatedList[index].endMinutes = value;
          setDayList(updatedList);
        }}
        toggle={() => {
          const updatedList: Day[] = [...dayList];
          updatedList[index].enabled = !updatedList[index].enabled;
          setDayList(updatedList);
        }}
      />}
    />
  </View>;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite500,
    paddingHorizontal: 16,
  },
  parameterTitle: {
    fontSize: 10,
    lineHeight: 15,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    flex: 1,
  },
  timeTitle: {
    alignSelf: 'center',
    textAlign: 'center',
  },
  item: {
    borderRadius: 5,
    backgroundColor: Colors.light.hereworksWhite200,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 8,
  },
  itemDisabled: {
    backgroundColor: Colors.light.hereworksBlack50,
  },
  titleSection: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  title: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginHorizontal: 16,
  },
  time: {
    borderRadius: 4,
    backgroundColor: '#FFF',
    padding: 12,
    marginHorizontal: 8,
  },
  input: {
    width: 20,
    height: 24,
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  colon: {
    marginHorizontal: 8,
  },
});

export default WorkingHoursSettings;
